<script setup lang="ts">
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    fr: '/conditions-utilisation',
  },
})

definePageMeta({
  name: 'TermsOfUsePage',
})

const route = useRoute()
const config = useRuntimeConfig()
const localePath = useLocalePath()

useHead({
  title: 'Conditions d’utilisation - Glouton',
  meta: [
    { name: 'description', content: 'Consultez les conditions encadrant l’utilisation du site Web, des applications et des services de Glouton.' },
    { property: 'og:title', content: 'Conditions d’utilisation - Glouton' },
    { property: 'og:description', content: 'Consultez les conditions encadrant l’utilisation du site Web, des applications et des services de Glouton.' },
    { property: 'og:url', content: computed(() => `${config.public.domain}${route.path}`) },
    { property: 'og:image', content: 'https://glouton.b-cdn.net/site/images/social.jpg' },
  ],
})
</script>

<template>
  <div id="TermsOfUsePage" class="bg-sel">
    <UContainer class="py-6 lg:py-8">
      <article class="max-w-4xl mx-auto bg-white rounded-lg px-5 py-6 sm:px-7 lg:px-10">
        <header class="border-b border-poivre-200 pb-4">
          <h1 class="text-2xl font-extrabold leading-tight text-poivre-800 sm:text-3xl font-title">
            Conditions d’utilisation de Glouton
          </h1>
          <p class="mt-2 text-sm text-poivre-500">
            Dernière mise à jour : 18 septembre 2026
          </p>
        </header>

        <div class="mt-6 space-y-8 text-sm leading-6 text-poivre-700">
          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              1. À propos de ces conditions
            </h2>
            <div class="space-y-4">
              <p>
                Glouton est exploité par <strong>Recettes Glouton Inc.</strong>, au <strong>5455, av. De Gaspé, bureau 710, Montréal (Québec) H2T 3B3</strong>. Pour nous joindre : <a href="mailto:support@glouton.app" class="text-tomate-600 underline hover:text-tomate-700">support@glouton.app</a>.
              </p>
              <p>
                Ces conditions encadrent l’utilisation de notre site Web et de nos applications. Leur acceptation est requise pour utiliser Glouton.
              </p>
              <p>
                Notre <ULink :to="localePath({ name: 'PrivacyPolicyPage' })" class="text-tomate-600 underline hover:text-tomate-700">Politique de confidentialité</ULink> explique comment nous traitons vos renseignements personnels. <strong>Accepter ces conditions ne signifie pas accepter le suivi publicitaire ou l’infolettre.</strong> Ces choix sont proposés séparément; les refuser ne vous empêche pas d’utiliser les services qui n’en dépendent pas.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              2. Nos services et votre compte
            </h2>
            <div class="space-y-4">
              <p>
                Glouton vous aide à repérer les rabais, consulter les circulaires, découvrir des recettes et organiser vos achats. Certaines fonctions sont gratuites; d’autres nécessitent un compte ou un abonnement Extra. L’offre choisie précise ce qui est inclus.
              </p>
              <p>
                Vous pouvez consulter les circulaires sans compte. Certaines analyses et fonctions de sauvegarde ou de synchronisation nécessitent une connexion.
              </p>
              <p>
                Utilisez une adresse courriel valide, protégez vos identifiants et signalez-nous tout accès suspect. Vous pouvez choisir un nom d’affichage sans utiliser votre nom légal. Pour collaborer avec vos proches, utilisez les fonctions de partage prévues.
              </p>
              <p>
                Vous devez avoir la capacité juridique de conclure l’entente ou obtenir l’autorisation de votre parent ou tuteur lorsque nécessaire, notamment pour un abonnement payant.
              </p>
              <p>
                Si vous avez moins de 14 ans, votre parent ou tuteur doit autoriser la création de votre compte et consentir à la collecte de vos renseignements personnels, sauf dans les cas permis par la loi.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              3. Rabais, prix et évaluations
            </h2>
            <div class="space-y-4">
              <p>
                Nous apportons du soin aux renseignements et aux analyses publiés. Des erreurs, changements de prix ou variations selon les magasins peuvent toutefois survenir.
              </p>
              <p>
                Avant un achat, vérifiez le commerce, les dates, le format, la disponibilité et les conditions de l’offre, notamment les coupons, cartes de fidélité ou quantités requises. Tenez aussi compte des taxes, consignes et autres frais applicables.
              </p>
              <p>
                Nos notes et classements reflètent notre méthode et les données disponibles. Ils vous aident à comparer les offres, sans garantir le prix le plus bas partout ni une économie précise. Les totaux, prix au poids et économies estimées dépendent des quantités et des hypothèses utilisées.
              </p>
              <p>
                Une liste d’épicerie n’est ni une commande ni une réservation. Les offres à venir peuvent changer. Glouton ne vend pas les produits présentés dans les circulaires : vos achats se font auprès des commerçants concernés. Signalez-nous une erreur à <a href="mailto:support@glouton.app" class="text-tomate-600 underline hover:text-tomate-700">support@glouton.app</a>.
              </p>
              <p>
                Dans les commerces de liquidation, certains produits peuvent être des surplus, avoir un emballage imparfait ou une date de conservation rapprochée. Vérifiez leur état et les conditions en magasin.
              </p>
              <p>
                Les noms, marques et logos des commerces présentés appartiennent à leurs titulaires respectifs. Sauf indication expresse, Glouton n’est ni affilié, ni associé, ni partenaire de ces commerces. Leur présence dans le service ne constitue pas une recommandation réciproque.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              4. Recettes et intelligence artificielle
            </h2>
            <div class="space-y-4">
              <p>
                Les recettes, suggestions, portions, coûts et valeurs nutritionnelles sont fournis à titre informatif. Les valeurs nutritionnelles sont des estimations et peuvent différer des valeurs réelles selon les produits, les quantités, les substitutions et la préparation.
              </p>
              <p>
                Nous pouvons utiliser des outils d’extraction et d’intelligence artificielle pour traiter des recettes et des circulaires, notamment lors de l’importation d’un lien, d’un texte ou d’une image. Ces outils peuvent commettre des erreurs ou omettre des renseignements, notamment sur les ingrédients, les allergènes, les quantités, les valeurs nutritionnelles, les substitutions ainsi que les temps et températures de cuisson ou de conservation.
              </p>
              <p>
                Avant de préparer ou de consommer un aliment, vérifiez le résultat, l’étiquette officielle des produits, les allergènes et les consignes de salubrité. Respectez les températures internes de cuisson et les méthodes de conservation appropriées. Un filtre ou une suggestion ne garantit pas l’absence d’allergènes ou de contamination croisée.
              </p>
              <p>
                Glouton ne fournit aucun avis médical, nutritionnel ou diététique. Si vous avez une allergie, une intolérance, une grossesse ou une condition médicale pouvant être influencée par votre alimentation, consultez un professionnel de la santé qualifié et ne vous fiez pas uniquement aux renseignements du service.
              </p>
              <p>
                Les recettes provenant de sites tiers restent soumises aux droits de leurs auteurs et peuvent changer ou devenir inaccessibles. Le traitement des données par nos fournisseurs est expliqué dans notre <ULink :to="localePath({ name: 'PrivacyPolicyPage' })" class="text-tomate-600 underline hover:text-tomate-700">Politique de confidentialité</ULink>.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              5. Vos contenus et vos importations
            </h2>
            <div class="space-y-4">
              <p>
                Vous conservez les droits que vous détenez sur vos contenus. Importer une recette ne vous transfère pas les droits de son auteur. Vous devez disposer des droits ou permissions nécessaires à l’utilisation que vous en faites et respecter la vie privée d’autrui.
              </p>
              <p>
                Vous autorisez Glouton et les fournisseurs nécessaires à son fonctionnement à enregistrer, traiter, mettre en forme et afficher vos contenus <strong>uniquement pour fournir les fonctions que vous demandez</strong>, comme importer une recette ou synchroniser une liste. Cette permission est non exclusive et sans redevance.
              </p>
              <p>
                <strong>Le contenu extrait et les personnalisations associés à votre compte ne sont pas ajoutés à notre catalogue public ni utilisés dans nos publicités.</strong> Lorsque vous importez une recette depuis une source publique, Glouton peut conserver des renseignements sur cette source, comme son adresse, son titre et son image, notamment afin de la reconnaître si elle est importée de nouveau. Lorsque vous utilisez une fonction de partage, les personnes que vous choisissez peuvent accéder au contenu partagé. Les accès techniques nécessaires sont encadrés par notre <ULink :to="localePath({ name: 'PrivacyPolicyPage' })" class="text-tomate-600 underline hover:text-tomate-700">Politique de confidentialité</ULink>.
              </p>
              <p>
                Après suppression d’un contenu, cette permission cesse, sous réserve des opérations de suppression, des sauvegardes temporaires et des obligations légales décrites dans cette politique. Des copies déjà partagées peuvent demeurer chez leurs destinataires.
              </p>
              <p>
                Pour signaler un contenu portant atteinte à vos droits, écrivez à <a href="mailto:support@glouton.app" class="text-tomate-600 underline hover:text-tomate-700">support@glouton.app</a> en précisant le contenu et le motif. Nous examinerons votre demande et pourrons en restreindre l’accès pendant la vérification.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              6. Abonnement Extra et paiements
            </h2>
            <div class="space-y-4">
              <p>
                Les modalités de l’offre sont présentées avant la souscription : fonctions incluses, prix, taxes et frais applicables, durée, fréquence de facturation, renouvellement et conditions d’annulation et de remboursement. Elles font partie de votre entente avec Glouton. Vous pouvez vérifier et corriger votre choix avant de confirmer et recevez une confirmation que vous pouvez conserver.
              </p>
              <p>
                Lorsqu’un essai ou une promotion est offert, ses conditions précisent sa durée, les critères d’admissibilité et, s’il y a lieu, le montant et la date du premier paiement. Un renouvellement automatique doit être prévu dans les modalités acceptées, avec les avis et autorisations requis.
              </p>
              <p>Pour arrêter le renouvellement :</p>
              <ul class="ml-6 list-disc space-y-2">
                <li>
                  <strong>Achat sur le Web :</strong> utilisez les options de gestion de votre abonnement dans votre profil ou écrivez à <a href="mailto:support@glouton.app" class="text-tomate-600 underline hover:text-tomate-700">support@glouton.app</a>.
                </li>
                <li>
                  <strong>Achat dans l’application :</strong> gérez l’abonnement auprès d’Apple ou de Google, selon la plateforme utilisée pour l’achat.
                </li>
              </ul>
              <p>
                L’arrêt du renouvellement est distinct d’une demande de résiliation immédiate ou de remboursement. La date de fin d’accès et les effets de votre demande vous sont précisés. Pour un problème de facturation ou une demande de remboursement, contactez-nous; si l’achat a été fait auprès d’Apple ou de Google, nous vous orienterons vers leur procédure lorsque nécessaire. Vos droits prévus par la loi demeurent applicables.
              </p>
              <p>
                <strong>Désinstaller l’application n’annule pas l’abonnement.</strong> Si vous supprimez votre compte, vérifiez aussi l’annulation de l’abonnement auprès de la plateforme d’achat, notamment Apple ou Google. Contactez-nous en cas de doute.
              </p>
              <p>
                Si une organisation vous offre Extra, les conditions de cette offre précisent sa durée. Sa fin ne crée pas automatiquement un abonnement à vos frais. Cette offre ne signifie pas que Glouton recommande les autres produits ou services de l’organisation.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              7. Utilisation permise et protection des contenus
            </h2>
            <div class="space-y-4">
              <p>
                Glouton est destiné à un usage personnel et familial, selon l’offre choisie. Un usage professionnel ou commercial nécessite notre autorisation s’il n’est pas expressément inclus dans l’offre.
              </p>
              <p>Sauf autorisation écrite, vous ne pouvez pas :</p>
              <ul class="ml-6 list-disc space-y-2">
                <li>contourner la connexion, les limites d’utilisation ou les protections du service;</li>
                <li>accéder sans autorisation à un compte ou perturber le fonctionnement de Glouton;</li>
                <li>extraire systématiquement nos analyses, historiques ou contenus par robot, script ou collecte organisée;</li>
                <li>revendre ou republier en masse les contenus ainsi extraits, ou les utiliser pour alimenter un service concurrent;</li>
                <li>utiliser les contenus protégés de Glouton pour entraîner une IA ou constituer un jeu de données;</li>
                <li>transmettre des contenus illégaux, harcelants ou portant atteinte aux droits d’autrui.</li>
              </ul>
              <p>
                Ces restrictions ne visent pas le partage de liens ni l’utilisation normale d’outils d’accessibilité.
              </p>
              <p>
                Les marques, textes originaux, interface, code et autres éléments protégés de Glouton lui appartiennent ou sont utilisés avec autorisation. Les contenus de tiers restent la propriété de leurs titulaires. La présence d’une marque ou d’un lien n’implique pas un partenariat.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              8. Disponibilité, suspension et fermeture du compte
            </h2>
            <div class="space-y-4">
              <p>
                Le fonctionnement de Glouton dépend notamment de fournisseurs d’hébergement, de paiement, de données, de communications et de boutiques d’applications. Des interruptions peuvent survenir en raison d’une maintenance, d’un problème technique ou de l’indisponibilité d’un service tiers. Nous ne garantissons pas un accès continu ou sans erreur.
              </p>
              <p>
                Dans les limites permises par la loi, Glouton n’est pas responsable d’un retard ou d’une interruption causé par un événement hors de son contrôle raisonnable, notamment une panne majeure d’un fournisseur essentiel, une cyberattaque, une catastrophe naturelle, un conflit, une pandémie ou une décision gouvernementale. Cette disposition ne limite pas vos garanties légales ni les recours obligatoires liés à un service payant.
              </p>
              <p>
                En cas de fraude, de risque de sécurité, de non-paiement ou de violation importante des conditions, nous pouvons restreindre l’accès de façon proportionnée. Lorsque possible, nous vous expliquons le problème et vous permettons de le corriger. Une urgence peut nécessiter une intervention immédiate. Toute résiliation par Glouton respecte les motifs, préavis et remboursements prévus par la loi.
              </p>
              <p>
                Vous pouvez demander la révision d’une restriction à <a href="mailto:support@glouton.app" class="text-tomate-600 underline hover:text-tomate-700">support@glouton.app</a>. Vous pouvez supprimer votre compte dans la section <strong>Profil</strong> ou par courriel. Notre <ULink :to="localePath({ name: 'PrivacyPolicyPage' })" class="text-tomate-600 underline hover:text-tomate-700">Politique de confidentialité</ULink> explique le traitement des données après la fermeture; la section Extra ci-dessus explique les démarches concernant l’abonnement.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              9. Responsabilité et désaccords
            </h2>
            <div class="space-y-4">
              <p>
                Les limites pratiques décrites dans ces conditions ne nous dégagent pas de nos obligations légales. Vos garanties et recours prévus par la loi demeurent applicables.
              </p>
              <p>
                Ces conditions sont régies par les lois du Québec et les lois fédérales canadiennes applicables, sous réserve des protections obligatoires dont vous bénéficiez ailleurs.
              </p>
              <p>
                Vous pouvez nous contacter pour chercher une solution à l’amiable, sans que cette démarche soit obligatoire avant d’exercer un recours. Nous n’imposons ni arbitrage obligatoire ni renonciation à une action collective. Si une clause est inapplicable, les autres demeurent en vigueur dans la mesure permise par la loi.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              10. Modifications
            </h2>
            <div class="space-y-4">
              <p>
                Les changements à ces conditions seront annoncés sur notre site ou dans l’application, avec leur date d’entrée en vigueur. Lorsque la loi exige un avis individuel, votre accord ou un droit de refus ou de résiliation, nous respectons ces exigences.
              </p>
              <p>
                La publication d’une nouvelle version ne modifie pas à elle seule les modalités d’un abonnement en cours. Ces conditions ne nous accordent pas un pouvoir général de modifier unilatéralement votre contrat.
              </p>
            </div>
          </section>

          <section>
            <h2 class="mb-3 text-xl font-bold text-poivre-800 font-title">
              11. Nous joindre
            </h2>
            <div class="space-y-4">
              <p>
                Pour toute question concernant ces conditions ou les services de Glouton, écrivez à <a href="mailto:support@glouton.app" class="text-tomate-600 underline hover:text-tomate-700">support@glouton.app</a>.
              </p>
              <address class="not-italic">
                <strong>Recettes Glouton Inc.</strong><br>
                5455, av. De Gaspé, bureau 710<br>
                Montréal (Québec) H2T 3B3
              </address>
            </div>
          </section>
        </div>
      </article>
    </UContainer>
  </div>
</template>
